import type { RouteLocationNormalizedLoaded } from "vue-router";
import { getSuccessCaseById } from "@/data/successCases";

const COMPANY_NAME = "郑州云轩网络科技有限公司";
const DEFAULT_DESCRIPTION =
  "郑州云轩网络科技有限公司提供软件开发、AI 技术应用、企业管理系统、移动应用及数字化解决方案服务。";

interface SeoMeta {
  title: string;
  description: string;
  noindex?: boolean;
}

const CANONICAL_ORIGIN = "https://yunxuanwl.cn";

function routeSeo(route: RouteLocationNormalizedLoaded): SeoMeta {
  if (route.name === "SuccessCaseDetail") {
    const rawId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
    const detail = getSuccessCaseById(Number(rawId));
    if (detail) {
      return {
        title: `${detail.title}｜成功案例｜${COMPANY_NAME}`,
        description: detail.description,
      };
    }
  }
  return {
    title: typeof route.meta.title === "string" ? route.meta.title : COMPANY_NAME,
    description:
      typeof route.meta.description === "string"
        ? route.meta.description
        : DEFAULT_DESCRIPTION,
    noindex: route.meta.noindex === true,
  };
}

function setMeta(name: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setPropertyMeta(property: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`
  );
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(path: string, noindex: boolean) {
  const existing = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  );
  if (noindex) {
    existing?.remove();
    return;
  }
  const element = existing ?? document.createElement("link");
  element.rel = "canonical";
  element.href = path === "/" ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${path}`;
  if (!existing) document.head.appendChild(element);
}

function setStructuredData(path: string, seo: SeoMeta) {
  let element = document.head.querySelector<HTMLScriptElement>("#structured-data");
  if (!element) {
    element = document.createElement("script");
    element.id = "structured-data";
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  const canonical = path === "/" ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${path}`;
  element.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": path === "/" ? "WebSite" : "WebPage",
    name: seo.title,
    description: seo.description,
    url: canonical,
    inLanguage: "zh-CN",
    publisher: {
      "@type": "Organization",
      name: COMPANY_NAME,
      url: `${CANONICAL_ORIGIN}/`,
      logo: `${CANONICAL_ORIGIN}/brand-mark.svg`,
    },
  });
}

export function updateDocumentSeo(route: RouteLocationNormalizedLoaded) {
  const seo = routeSeo(route);
  document.title = seo.title;
  setMeta("description", seo.description);
  setMeta("robots", seo.noindex ? "noindex,follow" : "index,follow");
  setPropertyMeta("og:site_name", COMPANY_NAME);
  setPropertyMeta("og:title", seo.title);
  setPropertyMeta("og:description", seo.description);
  setPropertyMeta("og:type", route.name === "SuccessCaseDetail" ? "article" : "website");
  setPropertyMeta(
    "og:url",
    route.path === "/" ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${route.path}`
  );
  setCanonical(route.path, seo.noindex === true);
  setStructuredData(route.path, seo);
}
